import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';

/** アクション別スプライトシート定義 */
const ZAAG_SHEETS = [
  { sheetKey: 'ss_zaag_idle',    animKey: 'zaag_idle',    label: 'IDLE',    frames: [0, 1, 2],       frameRate: 4, repeat: -1 },
  { sheetKey: 'ss_zaag_attack',  animKey: 'zaag_attack',  label: 'ATTACK',  frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 },
  { sheetKey: 'ss_zaag_special', animKey: 'zaag_special', label: 'SPECIAL', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 },
  { sheetKey: 'ss_zaag_move_f',  animKey: 'zaag_move_f',  label: 'MOVE_F',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 },
  { sheetKey: 'ss_zaag_move_b',  animKey: 'zaag_move_b',  label: 'MOVE_B',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 },
  { sheetKey: 'ss_zaag_dodge',   animKey: 'zaag_dodge',   label: 'DODGE',   frames: [0, 1, 2],       frameRate: 8, repeat:  0 },
  { sheetKey: 'ss_zaag_hit',     animKey: 'zaag_hit',     label: 'HIT',     frames: [0, 1, 2],       frameRate: 6, repeat:  0 },
  { sheetKey: 'ss_zaag_down',    animKey: 'zaag_down',    label: 'DOWN',    frames: [0, 1, 2],       frameRate: 4, repeat:  0 },
] as const;

/**
 * スプライトシート動作確認用シーン（開発用）
 *
 * zaagの全アクションアニメーションを切り替えて確認する。
 */
export class SpriteTestScene extends BaseScene {
  private sprite?: Phaser.GameObjects.Sprite;
  private animLabel?: Phaser.GameObjects.Text;
  private currentAnimIndex = 0;

  constructor() {
    super(SceneKey.SPRITE_TEST);
  }

  create(): void {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2 - 30;

    // 背景
    this.add.rectangle(cx, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x1a1a2e);

    // タイトル
    this.add
      .text(cx, 30, 'Sprite Test — zaag', {
        fontSize: '24px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // アニメーション登録（アクション別シートから）
    for (const def of ZAAG_SHEETS) {
      if (!this.anims.exists(def.animKey)) {
        this.anims.create({
          key: def.animKey,
          frames: this.anims.generateFrameNumbers(def.sheetKey, { frames: [...def.frames] }),
          frameRate: def.frameRate,
          repeat: def.repeat,
        });
      }
    }

    // スプライト表示（idle シートで初期化）
    this.sprite = this.add.sprite(cx, cy, ZAAG_SHEETS[0].sheetKey, 0);
    this.sprite.setScale(0.6);

    // ループしないアニメが完了したら idle に戻す
    this.sprite.on('animationcomplete', () => {
      this.playAnimation(0);
    });

    // 現在のアニメーション名
    this.animLabel = this.add
      .text(cx, cy + 160, '', {
        fontSize: '20px',
        color: '#ffcc00',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // 操作ボタン
    this.createButtons(cy + 210);

    // 操作説明
    this.add
      .text(cx, GAME_HEIGHT - 20, '1〜8キーでアニメ切替', {
        fontSize: '13px',
        color: '#666688',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5);

    // 戻るボタン
    this.createBackButton();

    // キーボードショートカット
    const keys = ['ONE','TWO','THREE','FOUR','FIVE','SIX','SEVEN','EIGHT'];
    keys.forEach((k, i) => {
      this.input.keyboard?.on(`keydown-${k}`, () => this.playAnimation(i));
    });

    // 初期アニメーション
    this.playAnimation(0);
  }

  private playAnimation(index: number): void {
    const def = ZAAG_SHEETS[index];
    if (!def) return;
    this.currentAnimIndex = index;

    // シートが変わる場合はテクスチャを切り替えてからアニメ再生
    if (this.sprite?.texture.key !== def.sheetKey) {
      this.sprite?.setTexture(def.sheetKey, 0);
    }
    this.sprite?.play(def.animKey);
    this.animLabel?.setText(`▶ ${def.label}`);
  }

  private createButtons(y: number): void {
    const buttonWidth = 90;
    const gap = 8;
    const cols = 4;
    const rows = Math.ceil(ZAAG_SHEETS.length / cols);
    const totalW = cols * buttonWidth + (cols - 1) * gap;
    const startX = (GAME_WIDTH - totalW) / 2 + buttonWidth / 2;

    ZAAG_SHEETS.forEach((def, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (buttonWidth + gap);
      const rowY = y + row * 50;

      const bg = this.add
        .rectangle(x, rowY, buttonWidth, 38, 0x334466)
        .setInteractive({ useHandCursor: true });

      this.add
        .text(x, rowY, `${i + 1}:${def.label}`, {
          fontSize: '13px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      bg.on('pointerover', () => bg.setFillStyle(0x4466aa));
      bg.on('pointerout', () => bg.setFillStyle(0x334466));
      bg.on('pointerdown', () => this.playAnimation(i));
    });
  }

  private createBackButton(): void {
    const bg = this.add
      .rectangle(70, 30, 100, 32, 0x553333)
      .setInteractive({ useHandCursor: true });

    this.add
      .text(70, 30, '← TITLE', {
        fontSize: '14px',
        color: '#ffaaaa',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    bg.on('pointerover', () => bg.setFillStyle(0x774444));
    bg.on('pointerout', () => bg.setFillStyle(0x553333));
    bg.on('pointerdown', () => this.transitionTo(SceneKey.TITLE));
  }
}
